/* @flow */
import Raven from "raven-js";

import type { Url } from "nexia_mobile/api/types";
import type { GeofenceCrossing } from "nexia_mobile/platforms/base";

export type AndroidGeofence = {
  id: string,
  name: string,
  latitude: string,
  longitude: string,
  radius: number, // int
  hrefAcknowledge: Url,
  hrefEnter: Url,
  hrefExit: Url,
};

function callBridge(method: string, ...args): any {
  const argsString = JSON.stringify(args);

  Raven.captureBreadcrumb({
    category: "AndroidAppInterface",
    level: "info",
    message: method,
    data: {
      args:
        argsString.length > 1024
          ? "[args hidden because size exceeded 1024 bytes]"
          : argsString,
    },
  });
  return window.AndroidAppInterface[method](...args);
}

// callBridgeAsync start
const callbacks: { [key: string]: (...args: Array<any>) => any } = {};

function registerCallback(callback: () => any): string {
  let callbackName = `callback_${_.uniqueId()}`;
  callbacks[callbackName] = callback;
  return callbackName;
}

export function invokeCallback(callbackName: string, value: any) {
  if (!callbacks[callbackName]) {
    return;
  }
  callbacks[callbackName](value);
  delete callbacks[callbackName];
}

function deleteCallback(callbackName: string) {
  delete callbacks[callbackName];
}

function callBridgeAsync(method: string, ...args): Promise<any> {
  return new Promise((resolve) => {
    const callbackName = registerCallback(resolve);
    callBridge(method, callbackName, ...args);
  });
}
// callBridgeAsync end

// JSON helpers
function tryParse(jsonString: string): any {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    throw new Error(
      `Unable to parse JSON string from Android Platform bridge: ${jsonString}`
    );
  }
}

function tryStringify(jsonValue: any): string {
  try {
    return JSON.stringify(jsonValue);
  } catch (error) {
    throw new Error(
      "Unable to stringify JSON value from Android Platform Bridge"
    );
  }
}
// JSON helpers end

// These all pass through to either callBridge or callBridgeAsync, but they have
// types that match the Java code, so that that type info is available to the
// rest of the world. They also handle JSON (de)serialization so that you don't
// have to think about JSON outside of this file and the Java code.
export default {
  enableRemoteDebugging: (): void => callBridge("enableRemoteDebugging"),
  notifyScriptInterfaceReady: (): void =>
    callBridge("notifyScriptInterfaceReady"),
  setPlatformAttributes: (): void => callBridge("setPlatformAttributes"),
  exitApp: (): void => callBridge("exitApp"),
  getStoredValue: (key: string): string => callBridge("getStoredValue", key),
  setStoredValue: (key: string, value: string): void =>
    callBridge("setStoredValue", key, value),
  clearStoredValue: (key: string): void => callBridge("clearStoredValue", key),
  clearStoredValues: (): void => callBridge("clearStoredValues"),
  saveImage: (
    base64Data: string
  ): Promise<{ hasPermission: boolean, message: string }> =>
    callBridgeAsync("saveImage", base64Data).then(tryParse),
  keepScreenOn: (): void => callBridge("keepScreenOn"),
  allowScreenDimming: (): void => callBridge("allowScreenDimming"),
  obtainPushNotificationToken: (): void =>
    callBridge("obtainPushNotificationToken"),
  clearNotifications: (): void => callBridge("clearNotifications"),
  isNotificationSoundConfigurable: (): boolean =>
    callBridge("isNotificationSoundConfigurable"),
  hideSoftKeyboard: (): void => callBridge("hideSoftKeyboard"),
  sendGoogleAnalyticsEvent: (
    category: string,
    action: string,
    label: string
  ): void => callBridge("sendGoogleAnalyticsEvent", category, action, label),
  sendGoogleAnalyticsTiming: (
    category: string,
    action: string,
    label: string,
    duration: number
  ): void =>
    callBridge("sendGoogleAnalyticsEvent", category, action, label, duration),
  checkLocationPermissions: (): Promise<boolean> =>
    callBridgeAsync("checkLocationPermissions"),
  areLocationServicesEnabled: (): Promise<boolean> =>
    callBridgeAsync("areLocationServicesEnabled"),
  getLocationServicesWarningMessage: (): Promise<{ message: ?string }> =>
    callBridgeAsync("getLocationServicesWarningMessage").then(tryParse),
  openAppSettings: (): void => callBridge("openAppSettings"),
  openLocationServiceSettings: (): void =>
    callBridge("openLocationServiceSettings"),
  openWifiSettings: (): void => callBridge("openWifiSettings"),
  setGeofences: (geofences: Array<AndroidGeofence>): void =>
    callBridge("setGeofences", tryStringify(geofences)),
  startGeofenceService: (): void => callBridge("startGeofenceService"),
  stopGeofenceService: (): void => callBridge("stopGeofenceService"),
  getGeofenceCrossings: (): Array<GeofenceCrossing> =>
    tryParse(callBridge("getGeofenceCrossings")),
  hasAppInstalled: (name: string): boolean =>
    callBridge("hasAppInstalled", name),
  wifiScan: (): Promise<Array<string>> =>
    callBridgeAsync("wifiScan").then(tryParse),
  connectToWifiNetwork: (ssid: string, password: string): void =>
    callBridge("connectToWifiNetwork", ssid, password),
  isFingerprintAuthAvailable: (): boolean =>
    callBridge("isFingerprintAuthAvailable"),
  authenticateWithFingerprint: (): Promise<{
    success: boolean,
    wasIntentionallyCancelled: boolean,
  }> => callBridgeAsync("authenticateWithFingerprint").then(tryParse),
  cancelFingerprintAuthentication: (): void =>
    callBridge("cancelFingerprintAuthentication"),
  nativeHTTPRequest: ({
    method,
    url,
    headers,
    body,
  }: {
    method: string,
    url: string,
    headers?: ?Object,
    body?: ?string,
  }): Promise<
    | { error: true, errorDescription: string }
    | { error: false, body: string, code: number }
  > =>
    callBridgeAsync(
      "nativeHTTPRequest",
      method,
      url,
      tryStringify(headers || {}),
      body
    )
      .then(decodeURIComponent)
      .then(tryParse),
  encryptStringFor724OpenAuth: (key: string, input: string): string =>
    callBridge("encryptStringFor724OpenAuth", key, input),
  showWebView: (): void => callBridge("showWebView"),
  hideWebView: (): void => callBridge("hideWebView"),
  enqueueIn: (callback: () => any, ms?: number = 0): string => {
    const callbackName = registerCallback(callback);
    callBridge("enqueueIn", callbackName, ms);
    return callbackName;
  },
  cancelEnqueued: (callbackName: string) => {
    deleteCallback(callbackName);
  },
};



// WEBPACK FOOTER //
// ./src/platforms/android/bridge.js